/**
 * Celebration — the "you got it" / "not this time" reveal beat.
 *
 * Lives during phase === 'reveal'. Shows the canonical answer, the delta
 * to the player's score, and a Continue button. Score does NOT decrement on a
 * wrong answer (v1 policy, audience-tuned).
 */
import { motion } from 'motion/react';

import { formatDollars, type BoardTileState } from '@/games/categories/engine';

import { celebrateSpring } from './motion';

import './Celebration.css';

export interface CelebrationProps {
  tile: BoardTileState;
  correct: boolean;
  delta: number;
  answerText: string;
  onContinue: () => void;
}

export function Celebration({
  tile,
  correct,
  delta,
  answerText,
  onContinue,
}: CelebrationProps): React.JSX.Element {
  return (
    <motion.section
      className="tb-celebrate"
      data-correct={correct ? 'true' : 'false'}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={celebrateSpring}
      aria-label="Result"
    >
      <motion.div
        className="tb-celebrate__prize"
        data-result={correct ? 'solved' : 'no-score'}
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ ...celebrateSpring, delay: 0.08 }}
        aria-hidden="true"
      >
        <span className="tb-celebrate__prize-label">
          {correct && delta > 0 ? 'Score won' : 'No score'}
        </span>
        <span className="tb-celebrate__prize-value">
          {correct && delta > 0 ? `+${formatDollars(delta)}` : '$0'}
        </span>
      </motion.div>

      <motion.h3
        className="tb-celebrate__verdict"
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        {correct ? 'Solved!' : 'No score.'}
      </motion.h3>

      <motion.p
        className="tb-celebrate__answer"
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.38, duration: 0.4 }}
      >
        <span className="tb-celebrate__answer-label">
          {correct ? 'Your answer:' : 'The answer was:'}
        </span>
        <strong className="tb-celebrate__answer-text">
          {correct && answerText ? answerText : tile.clue.canonicalAnswer}
        </strong>
      </motion.p>

      {!correct && answerText && (
        <motion.p
          className="tb-celebrate__yours"
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.44, duration: 0.4 }}
        >
          You said: <em>{answerText}</em>
        </motion.p>
      )}

      <motion.button
        type="button"
        className="tb-celebrate__continue"
        onClick={onContinue}
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.55, duration: 0.4 }}
        data-testid="continue"
      >
        Next clue
      </motion.button>
    </motion.section>
  );
}
